import { cleanKey, cleanPublicKey, pickPublicKey } from './public-key';

/**
 * Cross-framework env resolution for Supabase clients.
 *
 * This module exists because `@kairos/supabase` ships subpaths that need to
 * work on both Next.js (`process.env.NEXT_PUBLIC_*`) and Vite
 * (`import.meta.env.VITE_*`). The helpers here try each runtime's env surface
 * defensively — runtimes that don't define the other one must not crash.
 *
 * Runtime support matrix:
 *  - Next.js (server + client bundles): `process.env.NEXT_PUBLIC_*`
 *  - Vite / Rollup: `import.meta.env.VITE_*`
 *  - Electron renderer (Vite): `import.meta.env.VITE_*`
 *  - Electron main / Node scripts: `process.env.*` (caller supplies names)
 *  - Jest / Vitest (jsdom): whichever the test setup populates
 *
 * Nothing here should import React, framework-specific primitives, or the
 * generated database types — keep this file cheap and side-effect free.
 */

/**
 * Resolved pair used by every factory in the package.
 */
export interface ResolvedSupabaseEnv {
  url: string;
  anonKey: string;
}

/**
 * Env var names to read from each runtime. The defaults match the conventions
 * baked into the existing Next.js and Vite apps, but each factory can pass its
 * own names when it needs something different (e.g. the admin client wants
 * `SUPABASE_SERVICE_ROLE_KEY` — strictly server-side, no public prefix).
 *
 * Key migration (2026-04-26): Supabase introduced a new key model with
 * `*_PUBLISHABLE_KEY` (replaces anon) and `SUPABASE_SECRET_KEY` (replaces
 * service-role). The legacy keys keep working indefinitely, so we read the
 * new key first and fall back to the legacy one. Both formats are accepted
 * by `@supabase/supabase-js` interchangeably.
 */
export interface EnvVarNames {
  /** Name of the URL var, e.g. `NEXT_PUBLIC_SUPABASE_URL` or `VITE_SUPABASE_URL`. */
  url: string;
  /** Name of the legacy anon key var. Kept for backwards compatibility. */
  secret: string;
  /**
   * Name of the new publishable key var (e.g. `NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY`).
   * Tried FIRST when set; falls back to `secret` (the anon key) when absent.
   * Optional so callers that don't yet care about the migration keep working.
   */
  publishable?: string;
}

/**
 * Default var names for the browser / public clients.
 *
 * Resolution order: PUBLISHABLE_KEY → ANON_KEY (legacy fallback).
 */
export const VITE_PUBLIC_ENV: EnvVarNames = {
  url: 'VITE_SUPABASE_URL',
  secret: 'VITE_SUPABASE_ANON_KEY',
  publishable: 'VITE_SUPABASE_PUBLISHABLE_KEY',
};

export const NEXT_PUBLIC_ENV: EnvVarNames = {
  url: 'NEXT_PUBLIC_SUPABASE_URL',
  secret: 'NEXT_PUBLIC_SUPABASE_ANON_KEY',
  publishable: 'NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY',
};

/**
 * The trimmed value when it is a non-blank string, otherwise `undefined`.
 * Blank and whitespace-only values (a common CI/secret-store artefact) count
 * as missing so they can never shadow a valid fallback.
 */
const clean = cleanKey;

/**
 * Safely read `import.meta.env` without crashing under runtimes that don't
 * define `import.meta` (older bundlers, CJS loaders, some test environments).
 *
 * Uses `new Function` to avoid a static parse error in environments where
 * `import.meta` is invalid syntax outside of an ES module.
 *
 * IMPORTANT: For the default `VITE_SUPABASE_URL` / `VITE_SUPABASE_ANON_KEY`
 * names, we access `env.VITE_SUPABASE_URL` as a LITERAL member expression so
 * Vite's compile-time static replacement can see and inline them. Bracket
 * notation (`env[name]`) with a runtime variable defeats that replacement.
 * For caller-supplied custom names we fall back to bracket notation, which
 * works at runtime when the env object is already populated (e.g. Electron
 * main, Node scripts) but cannot rely on static replacement.
 */
export function readViteEnv(
  names: EnvVarNames = VITE_PUBLIC_ENV,
  envOverride?: Readonly<Record<string, string | undefined>>,
): ResolvedSupabaseEnv | null {
  try {
    const env = envOverride ?? readImportMetaEnv();
    if (!env) return null;

    // Literal-access fast path for the defaults — required for Vite's
    // compile-time static replacement.
    const useDefaults =
      !envOverride && names.url === VITE_PUBLIC_ENV.url && names.secret === VITE_PUBLIC_ENV.secret;

    const url = clean(useDefaults ? env.VITE_SUPABASE_URL : env[names.url]);

    // Publishable first; the legacy anon key only while it is absent. Both
    // literal accesses are necessary so Vite can statically replace each
    // when the value is set, even when `useDefaults` is true.
    const anonKey = useDefaults
      ? pickPublicKey([env.VITE_SUPABASE_PUBLISHABLE_KEY], [env.VITE_SUPABASE_ANON_KEY])
      : pickPublicKey(names.publishable ? [env[names.publishable]] : [], [env[names.secret]]);

    return url !== undefined && anonKey !== undefined ? { url, anonKey } : null;
  } catch {
    return null;
  }
}

function readImportMetaEnv(): Record<string, string | undefined> | null {
  const getMeta = new Function(
    'try { return typeof import.meta === "undefined" ? undefined : import.meta; } catch (_e) { return undefined; }'
  ) as () => { env?: Record<string, string | undefined> } | undefined;

  const meta = getMeta();
  if (!meta || typeof meta !== 'object') return null;
  const env = meta.env;
  return env && typeof env === 'object' ? env : null;
}

/**
 * Safely read `process.env` without crashing under runtimes where `process`
 * is not defined (browser builds that haven't polyfilled it).
 *
 * IMPORTANT: For the default `NEXT_PUBLIC_SUPABASE_URL` /
 * `NEXT_PUBLIC_SUPABASE_ANON_KEY` names, we access
 * `process.env.NEXT_PUBLIC_SUPABASE_URL` as a LITERAL member expression so
 * Next.js / Turbopack / Webpack's build-time env replacement can inline the
 * value into the client bundle. `process.env[name]` with a runtime variable
 * key is NOT replaced, which causes "Missing Supabase env vars" at runtime
 * even when the value is correctly set in `.env.local`.
 *
 * For caller-supplied custom names (e.g. the admin client's service role key)
 * we fall back to bracket notation, which works at runtime on the server
 * where `process.env` is the live Node env object.
 */
export function readNodeEnv(names: EnvVarNames = NEXT_PUBLIC_ENV): ResolvedSupabaseEnv | null {
  try {
    if (typeof process === 'undefined' || !process || !process.env) return null;

    // Literal-access fast path for the defaults — required for Next.js
    // compile-time static replacement in the client bundle.
    const useDefaults =
      names.url === NEXT_PUBLIC_ENV.url && names.secret === NEXT_PUBLIC_ENV.secret;

    const url = clean(
      useDefaults ? process.env.NEXT_PUBLIC_SUPABASE_URL : process.env[names.url],
    );

    const anonKey = useDefaults
      ? readNodePublicKey()
      : pickPublicKey(
          names.publishable ? [process.env[names.publishable]] : [],
          [process.env[names.secret]],
        );

    return url !== undefined && anonKey !== undefined ? { url, anonKey } : null;
  } catch {
    return null;
  }
}

/**
 * The public key variables on their own, for call sites that hand-roll a
 * client or a raw request (server routes, share pages, middleware).
 *
 * Both reads are LITERAL member expressions so Next.js can inline each
 * `NEXT_PUBLIC_*` value into client bundles. Returns `undefined` per field
 * when the variable is absent or blank; never reads a non-public variable.
 */
export function readNodePublicKeys(): {
  readonly publishable: string | undefined;
  readonly anon: string | undefined;
} {
  try {
    if (typeof process === 'undefined' || !process || !process.env) {
      return { publishable: undefined, anon: undefined };
    }
    return {
      publishable: cleanPublicKey(process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY),
      anon: cleanPublicKey(process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY),
    };
  } catch {
    return { publishable: undefined, anon: undefined };
  }
}

/** Publishable key if set, otherwise the legacy anon key, otherwise `undefined`. */
export function readNodePublicKey(): string | undefined {
  const { publishable, anon } = readNodePublicKeys();
  return publishable ?? anon;
}

/**
 * Explicit config overrides. When both fields are provided, env resolution is
 * skipped entirely. When either is missing, the fallback chain runs.
 */
export interface SupabaseEnvConfig {
  url?: string;
  anonKey?: string;
}

/**
 * Options for `resolveSupabaseEnv`.
 */
export interface ResolveEnvOptions {
  /** Vite env var names to try (default: `VITE_SUPABASE_URL` / `VITE_SUPABASE_ANON_KEY`). */
  vite?: EnvVarNames;
  /** Node/Next env var names to try (default: `NEXT_PUBLIC_SUPABASE_URL` / `NEXT_PUBLIC_SUPABASE_ANON_KEY`). */
  node?: EnvVarNames;
  /**
   * Subpath name used in the thrown error message, e.g. `'browser'`, `'admin'`,
   * `'public'`. Makes the error actionable when multiple factories fail.
   */
  subpath: string;
}

/**
 * Resolve Supabase connection config from (in order):
 *   1. Explicit `config` argument (when both fields are provided)
 *   2. Vite env vars (`import.meta.env.VITE_*`)
 *   3. Node/Next env vars (`process.env.NEXT_PUBLIC_*`)
 *   4. Throw a descriptive error
 *
 * The `typeof` guards inside `readViteEnv` / `readNodeEnv` ensure this works
 * under Next.js, Vite, Electron, and test runtimes without runtime errors on
 * the wrong stack.
 */
export function resolveSupabaseEnv(
  config: SupabaseEnvConfig | undefined,
  options: ResolveEnvOptions
): ResolvedSupabaseEnv {
  // 1. Explicit config wins when fully provided.
  // Explicit values are trimmed like env values; a blank one, or a server-only
  // secret key handed in by mistake, counts as missing and the env chain runs.
  const explicitUrl = clean(config?.url);
  const explicitKey = cleanPublicKey(config?.anonKey);
  if (explicitUrl !== undefined && explicitKey !== undefined) {
    return { url: explicitUrl, anonKey: explicitKey };
  }

  const viteNames = options.vite ?? VITE_PUBLIC_ENV;
  const nodeNames = options.node ?? NEXT_PUBLIC_ENV;

  // 2. Vite first (admin-dashboard, finance, recruitment, Electron renderer).
  const fromVite = readViteEnv(viteNames);
  if (fromVite) {
    return {
      url: explicitUrl ?? fromVite.url,
      anonKey: explicitKey ?? fromVite.anonKey,
    };
  }

  // 3. Node / Next.js fallback (dashboard, CMS, landing).
  const fromNode = readNodeEnv(nodeNames);
  if (fromNode) {
    return {
      url: explicitUrl ?? fromNode.url,
      anonKey: explicitKey ?? fromNode.anonKey,
    };
  }

  // 4. Nothing resolved — be explicit about every option.
  const viteKeyMsg = viteNames.publishable
    ? `${viteNames.publishable} (preferred) or ${viteNames.secret}`
    : viteNames.secret;
  const nodeKeyMsg = nodeNames.publishable
    ? `${nodeNames.publishable} (preferred) or ${nodeNames.secret}`
    : nodeNames.secret;
  throw new Error(
    `[@kairos/supabase/${options.subpath}] Missing Supabase env vars. Provide one of:\n` +
      `  • Vite: ${viteNames.url} + ${viteKeyMsg} (via import.meta.env)\n` +
      `  • Next.js: ${nodeNames.url} + ${nodeKeyMsg} (via process.env)\n` +
      `  • Or pass an explicit config: createClient({ url, anonKey })`
  );
}
